import React, { useContext, useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../services/AuthContext';
import { categories, slugify } from '../../services/categories'; 
import {
  collection,
  query,
  onSnapshot,
} from 'firebase/firestore';

const ENTRIES_COLLECTION = 'pdfEntries';

function CategoryList() {
  const navigate = useNavigate();
  const { db } = useContext(AuthContext);

  const [groupedEntries, setGroupedEntries] = useState({});

  useEffect(() => {
    if (!db) return;

    const q = query(collection(db, ENTRIES_COLLECTION));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const grouped = {};
      snapshot.docs.forEach((doc) => {
        const data = doc.data();
        const folder = data.folder;                   
        if (!grouped[folder]) grouped[folder] = [];
        grouped[folder].push(data);
      });
      setGroupedEntries(grouped);
    });

    return unsubscribe;
  }, [db]);

  const getStatus = (slug) => {
    const entries = groupedEntries[slug] || [];

    if (entries.length === 0) {
      return { color: '#95a5a6', label: 'Sin datos' };
    }

    const parseDate = (vencimiento) => {
      if (!vencimiento || typeof vencimiento !== 'string') return null;

      const str = vencimiento.trim();
      const parts = str.split(/[/\.-]/);
      if (parts.length !== 3) return null;

      let day, month, year;

      if (parts[0].length <= 2 && parts[1].length <= 2 && parts[2].length === 4) {
        day = parseInt(parts[0], 10);
        month = parseInt(parts[1], 10) - 1;
        year = parseInt(parts[2], 10);
      } else if (parts[0].length === 4 && parts[2].length <= 2) {
        year = parseInt(parts[0], 10);
        month = parseInt(parts[1], 10) - 1;
        day = parseInt(parts[2], 10);
      } else {
        return null;
      }

      const date = new Date(year, month, day);
      if (isNaN(date.getTime())) return null;

      date.setHours(0, 0, 0, 0);
      return date;
    };

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const treintaDias = new Date(today);
    treintaDias.setDate(today.getDate() + 45);

    const tieneVencido = entries.some((e) => {
      const fecha = parseDate(e.vencimiento);
      return fecha && fecha < today;
    });

    const estaProximoAVencer = entries.some((e) => {
      const fecha = parseDate(e.vencimiento);
      return fecha && fecha >= today && fecha <= treintaDias;
    });

    const tieneIncumplido = 
      entries.some((e) => 
        e.estadoGestion === 'No cumple' || e.auditoria2025 === 'No cumple'
      ) || tieneVencido;

    const tienePendiente = 
      entries.some((e) =>
        ['En trámite C/Pendiente', 'En trámite S/Pendiente', 'Se debe adecuar'].includes(e.estadoGestion) ||
        ['En trámite C/Pendiente', 'En trámite S/Pendiente', 'Se debe adecuar'].includes(e.auditoria2025)
      ) || estaProximoAVencer;

    const todosInformativos = entries.every(
      (e) => e.estadoGestion === 'Informativo' && e.auditoria2025 === 'Informativo'
    );

    if (tieneIncumplido) return { color: '#e74c3c', label: 'Incumplido' };
    if (tienePendiente) return { color: '#f3e412', label: 'En proceso' };
    if (todosInformativos) return { color: '#3498db', label: 'Informativo' };
    return { color: '#2ecc71', label: 'Cumple' };
  };

  const categoryStatuses = useMemo(() => {
    const statuses = {};
    categories.forEach((cat) => {
      const slug = slugify(cat);
      statuses[slug] = getStatus(slug);
    });
    return statuses;
  }, [groupedEntries]);  

  const handleClick = (category) => {
    const slug = slugify(category);
    navigate(`/cauchari_cna/${slug}`);
  };

  return (
    <ul className="category-list">
      {categories.map((cat) => {
        const slug = slugify(cat);
        const status = categoryStatuses[slug];

        return (
          <li
            key={cat}
            onClick={() => handleClick(cat)}
            className="category-item"
          >
            <span
              className="status-dot"
              style={{ backgroundColor: status.color }}
              title={status.label}
            />
            <span className="category-name">{cat}</span>
          </li>
        );
      })}
    </ul>
  );
}

export default CategoryList;
